"use client";
import React, {
  ComponentProps,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import { BdAltArrowDown, BdAltArrowUp } from "solar-icon-react/bd";
import { AppContainer, FlexCenterCol } from "@/components/index";
import { cn } from "@/lib";

interface Props extends ComponentProps<"section"> {
  children: React.ReactNode;
  btnCLassName?: string;
  gradientColor?: string;
  btnId?: string;
  collapsible?: boolean;
}

const ExpandableText: React.FC<Props> = ({
  collapsible = false,
  gradientColor = "from-main-body",
  btnId = "expandable",
  ...props
}) => {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(!collapsible);
  const [isAnimating, setIsAnimating] = useState(false);

  // Heights for animation
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [expandedHeight, setExpandedHeight] = useState<number | null>(null);

  // Wrapper that we animate; content we clamp
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Use ref to track current expanded state for measureHeights
  const expandedRef = useRef(expanded);

  // Keep ref in sync with state
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  // Measure both collapsed (clamped) and expanded heights
  const measureHeights = () => {
    const wrapperEl = wrapperRef.current;
    const contentEl = contentRef.current;
    if (!wrapperEl || !contentEl) return;

    // Use the ref value instead of closure
    const currentlyExpanded = expandedRef.current;

    // Preserve current states
    const hadClamp = contentEl.classList.contains("line-clamp-3");
    const prevTransition = wrapperEl.style.transition;

    // Prevent accidental animation during measuring
    wrapperEl.style.transition = "none";

    // Ensure clamp for collapsed height
    if (!hadClamp) contentEl.classList.add("line-clamp-3");
    const collapsedH = contentEl.getBoundingClientRect().height;

    // Remove clamp for expanded height
    contentEl.classList.remove("line-clamp-3");
    const expandedH = contentEl.scrollHeight;

    // Restore clamp based on CURRENT state (from ref)
    if (!currentlyExpanded) contentEl.classList.add("line-clamp-3");

    // Restore transition
    // Force reflow before restoring transition to avoid glitch
    void wrapperEl.getBoundingClientRect();
    wrapperEl.style.transition = prevTransition;

    setCollapsedHeight(collapsedH);
    setExpandedHeight(expandedH);
  };

  useLayoutEffect(() => {
    measureHeights();
  }, []);

  // Recompute on window resize with debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handle = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        measureHeights();
      }, 150); // Debounce resize events
    };

    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("resize", handle);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isFromGoogle =
        document.referrer.includes("google.") ||
        window.location.search.includes("from=google") ||
        window.location.href.includes("#:~:text=");

      if (isFromGoogle) {
        setExpanded(true);

        if (wrapperRef.current) {
          wrapperRef.current.style.transition = "none";
          wrapperRef.current.style.height = "auto";
        }
        if (contentRef.current) {
          contentRef.current.classList.remove("line-clamp-3");
        }

        setTimeout(() => {
          if (wrapperRef.current) wrapperRef.current.style.transition = "";
        }, 500);
      }
    }
  }, []);

  // Animate height between measured values; limit overflow hidden to animation only
  const runToggleAnimation = (nextExpanded: boolean) => {
    const wrapperEl = wrapperRef.current;
    const contentEl = contentRef.current;
    if (!wrapperEl || !contentEl) return setExpanded(nextExpanded);

    const startH = nextExpanded
      ? (collapsedHeight ?? contentEl.offsetHeight)
      : (expandedHeight ?? contentEl.scrollHeight);

    const endH = nextExpanded
      ? (expandedHeight ?? contentEl.scrollHeight)
      : (collapsedHeight ?? contentEl.offsetHeight);

    setIsAnimating(true);

    wrapperEl.style.height = `${startH}px`;
    wrapperEl.style.overflow = "hidden";
    void wrapperEl.getBoundingClientRect();
    wrapperEl.style.transition = "height 400ms ease";

    if (nextExpanded) {
      contentEl.classList.remove("line-clamp-3");
    }

    requestAnimationFrame(() => {
      wrapperEl.style.height = `${endH}px`;
    });

    let hasCleanedUp = false;

    const cleanup = () => {
      if (hasCleanedUp) return; // Prevent double cleanup
      hasCleanedUp = true;

      wrapperEl.removeEventListener("transitionend", onEnd);
      clearTimeout(fallbackTimer); // Clear the fallback timer
      setExpanded(nextExpanded);
      setIsAnimating(false);

      wrapperEl.style.transition = "";
      wrapperEl.style.height = "";
      wrapperEl.style.overflow = "visible";

      if (!nextExpanded) {
        contentEl.classList.add("line-clamp-3");
      }
    };

    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName === "height") {
        cleanup();
      }
    };

    wrapperEl.addEventListener("transitionend", onEnd);

    // Fallback timer - only as a safety net, much longer than animation duration
    const fallbackTimer = setTimeout(() => {
      cleanup();
    }, 1000); // Increased from 450ms to 1000ms
  };

  return (
    <section
      {...props}
      className={cn("relative mx-auto w-full text-black", props.className)}
    >
      <AppContainer className="relative h-auto px-4 sm:px-6 md:px-8">
        <FlexCenterCol className="relative py-8 md:px-4">
          {/* Content wrapper with animated height */}
          <div className="relative">
            <div
              ref={wrapperRef}
              className="relative z-10 w-full"
              style={{ overflow: expanded ? "visible" : undefined }}
            >
              <div
                ref={contentRef}
                className={cn(
                  "w-full min-w-0",
                  collapsible && !expanded && "line-clamp-3",
                )}
              >
                {props.children}
              </div>
            </div>

            {/* Gradient mask when collapsed */}
            {collapsible && !expanded && (
              <div
                className={cn(
                  "pointer-events-none absolute bottom-0 left-0 z-20 h-[75%] w-full bg-gradient-to-t to-transparent",
                  gradientColor,
                )}
              />
            )}
          </div>

          {/* Toggle Button */}
          {collapsible && (
            <button
              id={btnId}
              onClick={() => {
                if (isAnimating) return;
                runToggleAnimation(!expanded);
              }}
              className={cn(
                "relative z-30 mx-auto mt-2 flex cursor-pointer items-center text-sm text-teal-300 md:text-base",
                props.btnCLassName,
              )}
              disabled={isAnimating}
            >
              {expanded ? t("footer.close") : t("footer.showMore")}
              <span className="flex-center">
                {expanded ? <BdAltArrowUp /> : <BdAltArrowDown />}
              </span>
            </button>
          )}
        </FlexCenterCol>
      </AppContainer>
    </section>
  );
};

export default ExpandableText;
